import {
  ApiClientError, buildUrl,
} from '@/errors';
import { getIdToken } from '@/lib/cognito-client';
import {
  usersResponseSchema,
  userResponseSchema,
  type UsersResponse,
  type UserResponse,
  type UserStatus,
  type UserRole,
  type InviteUserRequest,
  type UpdateUserRequest,
  type UserAction,
} from '../types/users';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// List users with filters
export interface UserFilters {
  readonly status?: UserStatus;
  readonly role?: UserRole;
  readonly search?: string;
  readonly supplierId?: string;
  readonly siteId?: string;
  readonly limit?: number;
  readonly nextToken?: string;
}

export async function getUsers(filters?: UserFilters): Promise<UsersResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.role) params.set('role', filters.role);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.supplierId) params.set('supplierId', filters.supplierId);
  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.nextToken) params.set('nextToken', filters.nextToken);

  const url = buildUrl(API_BASE_URL, '/v1/users', params);
  const response = await fetch(url, { headers: await getAuthHeaders() });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to fetch users');
  }

  return usersResponseSchema.parse(await response.json());
}

// Get single user
export async function getUser(userId: string): Promise<UserResponse> {
  const url = buildUrl(API_BASE_URL, `/v1/users/${userId}`);
  const response = await fetch(url, { headers: await getAuthHeaders() });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to fetch user');
  }

  return userResponseSchema.parse(await response.json());
}

// Invite new user
export async function inviteUser(data: InviteUserRequest): Promise<UserResponse> {
  const url = buildUrl(API_BASE_URL, '/v1/users/invite');
  const response = await fetch(url, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to invite user');
  }

  return userResponseSchema.parse(await response.json());
}

// Update user
export async function updateUser(userId: string, data: UpdateUserRequest): Promise<UserResponse> {
  const url = buildUrl(API_BASE_URL, `/v1/users/${userId}`);
  const response = await fetch(url, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to update user');
  }

  return userResponseSchema.parse(await response.json());
}

// Perform user action (disable, enable, reset password, resend invite)
export async function performUserAction(userId: string, action: UserAction): Promise<UserResponse> {
  const url = buildUrl(API_BASE_URL, `/v1/users/${userId}/actions`);
  const response = await fetch(url, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ action }),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, `Failed to ${action.replace('-', ' ')} user`);
  }

  return userResponseSchema.parse(await response.json());
}

// Convenience methods
export async function disableUser(userId: string): Promise<UserResponse> {
  return performUserAction(userId, 'disable');
}

export async function enableUser(userId: string): Promise<UserResponse> {
  return performUserAction(userId, 'enable');
}

export async function resetUserPassword(userId: string): Promise<UserResponse> {
  return performUserAction(userId, 'reset-password');
}

export async function resendInvitation(userId: string): Promise<UserResponse> {
  return performUserAction(userId, 'resend-invitation');
}

// Delete user
export async function deleteUser(userId: string): Promise<void> {
  const url = buildUrl(API_BASE_URL, `/v1/users/${userId}`);
  const response = await fetch(url, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to delete user');
  }
}
